// Segmentos guardados y campañas de mensajes uno a uno (Marketing M1-3).
import type { SupabaseClient } from '@supabase/supabase-js';
import { cargarHistoricas, cargarPedidos, type ProductoVenta } from './pedidos';
import { FILTRO_VACIO, activos, buscadorSku, resumenClientes, type FiltroClientes, type ResumenCliente } from '../logica/clientes';
import type { CanalMensajes, EstadoEnvio } from '../logica/mensajes';
import { hoy } from '../logica/fechas';

const ok = ({ error }: { error: { message: string } | null }) => { if (error) throw new Error(/duplicate key|unique/i.test(error.message) ? 'Ya hay un segmento con ese nombre.' : error.message); };

// Resumen de compras de todos los clientes (pedidos del sistema y compras anteriores).
export async function cargarResumen(sb: SupabaseClient, productos: Pick<ProductoVenta, 'sku' | 'nombre'>[]): Promise<Map<string, ResumenCliente>> {
  const [d, h] = await Promise.all([cargarPedidos(sb), cargarHistoricas(sb)]);
  return resumenClientes(activos([...d.clientes.values()]), d.pedidos, h, buscadorSku(productos));
}

export interface Segmento { id: number; nombre: string; filtros: FiltroClientes }
export async function cargarSegmentos(sb: SupabaseClient): Promise<Segmento[]> {
  const { data, error } = await sb.from('segmentos').select('id, nombre, filtros').order('nombre');
  if (error) throw new Error(error.message);
  return (data || []).map(s => ({ ...s, filtros: { ...FILTRO_VACIO, ...(s.filtros as Partial<FiltroClientes>) } }));
}
export async function crearSegmento(sb: SupabaseClient, nombre: string, filtros: FiltroClientes): Promise<Segmento> {
  const { data, error } = await sb.from('segmentos').insert({ nombre, filtros }).select('id, nombre, filtros').single();
  ok({ error });
  return { ...(data as Segmento), filtros };
}
export async function borrarSegmento(sb: SupabaseClient, id: number) { ok(await sb.from('segmentos').delete().eq('id', id)); }

export interface Destinatario { cliente_id: string; estado: EstadoEnvio; fecha: string | null }
export interface CampanaMensajes { id: string; nombre: string; canal: CanalMensajes; asunto: string | null; texto: string; creada: string; destinatarios: Destinatario[] }
export async function cargarCampanasMensajes(sb: SupabaseClient): Promise<CampanaMensajes[]> {
  const [c, d] = await Promise.all([
    sb.from('campanas').select('id, nombre, canal, asunto, texto, creada').order('id', { ascending: false }),
    sb.from('campana_destinatarios').select('campana_id, cliente_id, estado, fecha'),
  ]);
  const e = c.error || d.error;
  if (e) throw new Error(e.message);
  return (c.data || []).map(k => ({ ...k, destinatarios: (d.data || []).filter(x => x.campana_id === k.id).map(({ cliente_id, estado, fecha }) => ({ cliente_id, estado, fecha })) })) as CampanaMensajes[];
}
export async function crearCampanaMensajes(sb: SupabaseClient, datos: { nombre: string; canal: CanalMensajes; asunto: string; texto: string; filtros: FiltroClientes; clientes: string[] }): Promise<string> {
  const { data, error } = await sb.rpc('crear_campana_mensajes', { datos });
  if (error) throw new Error(error.message);
  return data as string;
}
// Marcar a un destinatario. "Pidió no recibir" también le quita la autorización de mensajes al cliente.
export async function marcarDestinatario(sb: SupabaseClient, campana: string, cliente: string, estado: EstadoEnvio) {
  ok(await sb.from('campana_destinatarios').update({ estado, fecha: estado === 'pendiente' ? null : hoy() }).eq('campana_id', campana).eq('cliente_id', cliente));
  if (estado === 'baja') ok(await sb.from('clientes').update({ acepta_mensajes: false, acepta_fecha: null }).eq('id', cliente));
}
export async function borrarCampanaMensajes(sb: SupabaseClient, id: string) {
  const { count, error } = await sb.from('pedidos').select('id', { count: 'exact', head: true }).eq('mensajes_id', id);
  if (error) throw new Error(error.message);
  if (count) throw new Error('Esta campaña ya tiene pedidos: no se borra.');
  ok(await sb.from('campanas').delete().eq('id', id));
}
